import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { getToken, decodeToken, hasAccessToProgram, getPermissions } from "../utils/secureStorage";
import { isRoleAllowed } from "../config/routePermissions";
import { getProgramCodeForRoute, isLegacyRole, getDashboardForUser } from "../config/routeConfig";
import Loader from "../components/loader";

const getRoleFromToken = (token) => {
  if (!token) return null;
  try {
    const payload = decodeToken(token);
    return payload?.role || null;
  } catch (error) {
    console.error("Error decoding token for role:", error);
    return null;
  }
};

const getEntityTypeFromToken = (token) => {
  if (!token) return null;
  try {
    const payload = decodeToken(token);
    return payload?.entity_type || payload?.entityType || null;
  } catch (error) {
    console.error("Error decoding token for entity type:", error);
    return null;
  }
};

const checkRouteAccess = (role, entityType, pathname) => {
  const normalizedEntityType = entityType?.toUpperCase();
  const normalizedRole = role?.toUpperCase();
  
  // PRIMARY: Check permission-based access for routes with program codes
  const programCode = getProgramCodeForRoute(pathname);
  if (programCode) {
    return hasAccessToProgram(programCode);
  }
  
  // SECONDARY: Use entityType for route permissions (if present)
  if (normalizedEntityType) {
    return isRoleAllowed(normalizedEntityType, pathname);
  }
  
  // TERTIARY: No entityType = treat as admin, check admin role routes
  if (!normalizedEntityType) {
    if (isLegacyRole(normalizedRole)) {
      return isRoleAllowed(role, pathname);
    }
    return true;
  }
  
  return true;
};

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    hasAccess: false,
    redirectPath: "/",
  });

  useEffect(() => {
    const checkAuth = () => {
      const token = getToken();
      if (!token) {
        setAuthState({ isAuthenticated: false, hasAccess: false, redirectPath: "/" });
        setIsChecking(false);
        return;
      }

      const role = getRoleFromToken(token);
      if (!role) {
        setAuthState({ isAuthenticated: false, hasAccess: false, redirectPath: "/" });
        setIsChecking(false);
        return;
      }

      const entityType = getEntityTypeFromToken(token);
      const permissions = getPermissions();

      let hasAccess = true;
      if (allowedRoles && allowedRoles.length > 0) {
        const entityOrRole = entityType?.toUpperCase() || role?.toUpperCase();
        hasAccess = allowedRoles.map((r) => r.toUpperCase()).includes(entityOrRole);
      } else {
        hasAccess = checkRouteAccess(role, entityType, location.pathname);
      }

      const redirectPath = getDashboardForUser(role, entityType, permissions);

      setAuthState({
        isAuthenticated: true,
        hasAccess,
        redirectPath,
        role,
        entityType,
      });
      setIsChecking(false);
    };
    checkAuth();
  }, [location.pathname, allowedRoles]);

  if (isChecking) {
    return <Loader />;
  }

  if (!authState.isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  if (!authState.hasAccess) {
    return <Navigate to={authState.redirectPath} replace />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
};

export default ProtectedRoute;
